import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal, Spinner } from 'react-bootstrap';
import { FaPlus, FaSave, FaTimes, FaClipboardList, FaCalendar, FaSearch } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { coverageAPI, branchesAPI, regionsAPI } from '../../utils/api';
import { getCurrentUser } from '../../utils/permissionUtils';
import notificationService from '../../services/notificationService';
import '../../styles/modern-design-system.css';
import '../admin/Coverage.css';

const Coverage = () => {
  const [coverages, setCoverages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [duplicateWarning, setDuplicateWarning] = useState('');
  
  const currentUser = getCurrentUser();
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Form state
  const [siteName, setSiteName] = useState('');
  const [branch, setBranch] = useState('');
  const [region, setRegion] = useState('');
  const [numColumns, setNumColumns] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isSingleDay, setIsSingleDay] = useState(false);
  const [guards, setGuards] = useState([{ name: '', phone: '', payType: 'PAYABLE', amount: 500 }]);

  // Fetch coverages on component mount
  useEffect(() => {
    fetchCoverages();
  }, []);

  // Load branches with server-side search and pagination
  const loadBranches = async (inputValue) => {
    try {
      const params = {};
      if (inputValue) {
        params.search = inputValue;
      }
      params.limit = 50;
      
      const response = await branchesAPI.getAll(params);
      const data = Array.isArray(response.data) ? response.data : response.data.data || [];
      return data.map(branch => ({
        value: branch.name,
        label: branch.name,
        id: branch.id
      }));
    } catch (err) {
      console.error('Error loading branches:', err);
      return [];
    }
  };

  // Load regions with server-side search and pagination
  const loadRegions = async (inputValue) => {
    try {
      const params = {};
      if (inputValue) {
        params.search = inputValue;
      }
      params.limit = 50;
      
      const response = await regionsAPI.getAll(params);
      const data = Array.isArray(response.data) ? response.data : response.data.data || [];
      return data.map(region => ({
        value: region.name,
        label: region.name,
        id: region.id
      }));
    } catch (err) {
      console.error('Error loading regions:', err);
      return [];
    }
  };

  const fetchCoverages = async () => {
    try {
      setLoading(true);
      const response = await coverageAPI.getAll();
      
      // Filter to only show coverages created by the current user
      // Check both creator.id and createdBy (in case backend uses different field names)
      const userCoverages = (response.data || []).filter(coverage => {
        const creatorId = coverage.creator?.id || coverage.createdBy?.id || coverage.createdBy;
        const userId = currentUser?.id || currentUser?.userId;
        
        return creatorId === userId || coverage.userId === userId;
      });
      
      setCoverages(userCoverages);
      setError('');
    } catch (err) {
      console.error('Error fetching coverages:', err);
      setCoverages([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper: check if two date ranges overlap (inclusive)
  const datesOverlap = (start1, end1, start2, end2) => {
    return new Date(start1) <= new Date(end2) && new Date(end1) >= new Date(start2);
  };

  // Check for duplicate guards against existing coverages
  // Returns { phoneConflicts, nameConflicts }
  const checkDuplicateGuards = (guardsToCheck, newDateFrom, newDateTo) => {
    const phoneConflicts = [];
    const nameConflicts = [];
    const phoneConflictKeys = new Set();

    const filledGuards = guardsToCheck.filter(g => g.name.trim() || g.phone.trim());

    coverages.forEach(coverage => {
      const existStart = coverage.dateFrom;
      const existEnd = coverage.isSingleDay ? coverage.dateFrom : (coverage.dateTo || coverage.dateFrom);

      if (!datesOverlap(newDateFrom, newDateTo, existStart, existEnd)) return;

      (coverage.guards || []).forEach(existGuard => {
        filledGuards.forEach(newGuard => {
          const newPhone = (newGuard.phone || '').trim();
          const existPhone = (existGuard.phone || '').trim();
          const newName = (newGuard.name || '').trim().toLowerCase();
          const existName = (existGuard.name || '').trim().toLowerCase();

          // Primary check: same phone number on an overlapping date
          if (newPhone && existPhone && newPhone === existPhone) {
            const key = `${newPhone}`;
            if (!phoneConflictKeys.has(key)) {
              phoneConflictKeys.add(key);
              phoneConflicts.push({
                guardName: (newGuard.name || newPhone).trim(),
                phone: newPhone,
                siteName: coverage.siteName,
                dateFrom: coverage.dateFrom,
                dateTo: coverage.isSingleDay ? coverage.dateFrom : (coverage.dateTo || coverage.dateFrom),
                isSingleDay: coverage.isSingleDay
              });
            }
          } else if (newName && existName && newName === existName) {
            // Secondary check: same name on an overlapping date (advisory only)
            if (!nameConflicts.some(c => c.guardName.toLowerCase() === newName)) {
              nameConflicts.push({
                guardName: newGuard.name.trim(),
                siteName: coverage.siteName,
                dateFrom: coverage.dateFrom,
                dateTo: coverage.isSingleDay ? coverage.dateFrom : (coverage.dateTo || coverage.dateFrom),
                isSingleDay: coverage.isSingleDay
              });
            }
          }
        });
      });
    });

    return { phoneConflicts, nameConflicts };
  };

  const formatConflictDate = (conflict) => {
    if (conflict.isSingleDay || conflict.dateFrom === conflict.dateTo) {
      return new Date(conflict.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return `${new Date(conflict.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(conflict.dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const handleAddCoverage = () => {
    setSiteName('');
    setBranch('');
    setRegion('');
    setNumColumns(1);
    setDateFrom('');
    setDateTo('');
    setIsSingleDay(false);
    setGuards([{ name: '', phone: '', payType: 'PAYABLE', amount: 500 }]);
    setShowModal(true);
    setError('');
    setSuccess('');
    setDuplicateWarning('');
  };

  const handleNumColumnsChange = (value) => {
    if (value === '') {
      setNumColumns('');
      return;
    }
    
    const num = parseInt(value);
    if (isNaN(num) || num < 1) {
      return;
    }
    
    setNumColumns(num);
    
    const currentGuards = [...guards];
    if (num > currentGuards.length) {
      const toAdd = num - currentGuards.length;
      for (let i = 0; i < toAdd; i++) {
        currentGuards.push({ name: '', phone: '', payType: 'PAYABLE', amount: 500 });
      }
    } else if (num < currentGuards.length) {
      currentGuards.splice(num);
    }
    setGuards(currentGuards);
  };

  const handleGuardChange = (index, field, value) => {
    const updatedGuards = [...guards];
    updatedGuards[index][field] = value;
    setGuards(updatedGuards);
  };

  const validateForm = () => {
    if (!siteName.trim()) {
      setError('Please enter a site name');
      return false;
    }
    
    if (!branch.trim()) {
      setError('Please enter a branch');
      return false;
    }
    
    if (!region.trim()) {
      setError('Please enter a region');
      return false;
    }
    
    if (isSingleDay) {
      if (!dateFrom) {
        setError('Please select a date');
        return false;
      }
    } else {
      if (!dateFrom || !dateTo) {
        setError('Please select both start and end dates');
        return false;
      }
      if (new Date(dateFrom) > new Date(dateTo)) {
        setError('End date must be after start date');
        return false;
      }
    }
    
    const hasCompleteGuard = guards.some(g => g.name.trim() && g.phone.trim());
    if (!hasCompleteGuard) {
      setError('Please add at least one guard with name and phone number');
      return false;
    }

    // Duplicate guard validation
    const effectiveEndDate = isSingleDay ? dateFrom : dateTo;
    const { phoneConflicts, nameConflicts } = checkDuplicateGuards(guards, dateFrom, effectiveEndDate);

    if (phoneConflicts.length > 0) {
      const header = phoneConflicts.length === 1
        ? 'This guard is already scheduled on the selected date:'
        : 'The following guards are already scheduled on the selected date:';
      const details = phoneConflicts.map(c =>
        `• ${c.guardName} (${c.phone}) is already assigned to ${c.siteName} on ${formatConflictDate(c)}.`
      ).join('\n');
      setError(`${header}\n${details}\n\nEach guard can only appear once per day. Please remove the duplicate before saving.`);
      return false;
    }

    if (nameConflicts.length > 0) {
      const details = nameConflicts.map(c =>
        `• ${c.guardName} appears to already be assigned to ${c.siteName} on ${formatConflictDate(c)} (with a different phone number).`
      ).join('\n');
      setDuplicateWarning(`Possible duplicate — please verify before saving:\n${details}\n\nIf this is the same person, remove the duplicate entry.`);
    } else {
      setDuplicateWarning('');
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDuplicateWarning('');
    
    if (!validateForm()) {
      return;
    }
    
    const coverageData = {
      siteName: siteName.trim(),
      branch: branch.trim(),
      region: region.trim(),
      numColumns,
      dateFrom,
      dateTo: isSingleDay ? dateFrom : dateTo,
      isSingleDay,
      guards: guards.filter(g => g.name.trim() || g.phone.trim())
    };
    
    try {
      const response = await coverageAPI.create(coverageData);
      
      // Trigger notification to admin and sub-admins
      notificationService.triggerCoverageAction('coverage-submitted', {
        personnelName: currentUser?.name || currentUser?.username || 'Personnel',
        personnelId: currentUser?.id || currentUser?.userId,
        siteName: coverageData.siteName,
        branch: coverageData.branch,
        region: coverageData.region
      });
      
      setSuccess('Coverage assignment created successfully!');
      setShowModal(false);
      // Fetch the updated list from the server to ensure sync
      await fetchCoverages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving coverage:', err);
      
      // Fallback: Save locally if backend is not ready
      const localCoverage = {
        id: Date.now(),
        ...coverageData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setCoverages([...coverages, localCoverage]);
      setSuccess('Coverage created locally (backend not connected)');
      setShowModal(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setDuplicateWarning('');
  };

  const formatDateRange = (from, to) => {
    const fromDate = new Date(from).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const toDate = new Date(to).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fromDate} - ${toDate}`;
  };

  const calculateDuration = (from, to) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffTime = Math.abs(toDate - fromDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  };

  // Get all sites a guard is assigned to
  const getGuardSiteAssignments = (guardName) => {
    if (!guardName) return [];
    
    const assignments = [];
    coverages.forEach(coverage => {
      const guardInCoverage = coverage.guards?.find(
        g => g.name && g.name.toLowerCase().trim() === guardName.toLowerCase().trim()
      );
      
      if (guardInCoverage) {
        assignments.push({
          siteName: coverage.siteName,
          dateFrom: coverage.dateFrom,
          dateTo: coverage.dateTo,
          isSingleDay: coverage.isSingleDay
        });
      }
    });
    
    return assignments;
  };

  // Get unique guards with multiple site assignments
  const getGuardsWithMultipleSites = () => {
    const guardSites = new Map();
    
    coverages.forEach(coverage => {
      coverage.guards?.forEach(guard => {
        if (guard.name && guard.name.trim()) {
          const guardKey = guard.name.toLowerCase().trim();
          
          if (!guardSites.has(guardKey)) {
            guardSites.set(guardKey, {
              name: guard.name.trim(),
              phone: guard.phone,
              sites: []
            });
          }
          
          guardSites.get(guardKey).sites.push({
            siteName: coverage.siteName,
            dateFrom: coverage.dateFrom,
            dateTo: coverage.dateTo,
            isSingleDay: coverage.isSingleDay
          });
        }
      });
    });
    
    // Filter only guards with multiple sites
    return Array.from(guardSites.values()).filter(guard => guard.sites.length > 1);
  };

  // Filter coverages based on search term and date filters
  const filteredCoverages = coverages.filter(coverage => {
    const matchesSearch = searchTerm === '' || 
      coverage.siteName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coverage.guards?.some(guard => 
        guard.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guard.phone?.includes(searchTerm)
      );
    
    let matchesDateRange = true;
    if (filterDateFrom || filterDateTo) {
      const coverageStart = new Date(coverage.dateFrom);
      const coverageEnd = new Date(coverage.dateTo);
      
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        matchesDateRange = matchesDateRange && (coverageEnd >= fromDate);
      }
      
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        matchesDateRange = matchesDateRange && (coverageStart <= toDate);
      }
    }
    
    return matchesSearch && matchesDateRange;
  });

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-2" style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-light))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                <FaClipboardList className="me-2" style={{ color: 'var(--accent-blue)' }} />
                My Coverage Assignments
              </h1>
              <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                Create and manage your guard coverage schedules • {coverages.length} {coverages.length === 1 ? 'assignment' : 'assignments'}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                className="d-flex align-items-center gap-2"
                onClick={handleAddCoverage}
                style={{
                  padding: '0.625rem 1.5rem',
                  fontWeight: 600
                }}
              >
                <FaPlus />
                Add Coverage
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Guards with Multiple Site Assignments - Commented out for now */}
      {/* {getGuardsWithMultipleSites().length > 0 && (
        <Card className="shadow-sm mb-4" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <h5 className="mb-3" style={{ 
              fontWeight: 700, 
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FaClipboardList style={{ color: 'var(--accent-blue)' }} />
              Guards Assigned to Multiple Sites
            </h5>
            <Row className="g-3">
              {getGuardsWithMultipleSites().map((guard, index) => (
                <Col key={index} md={6} lg={4}>
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1rem'
                  }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div>
                        <h6 style={{ 
                          fontWeight: 700, 
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                          fontSize: '1rem'
                        }}>
                          {guard.name}
                        </h6>
                        {guard.phone && (
                          <small style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            {guard.phone}
                          </small>
                        )}
                      </div>
                      <span className="badge" style={{
                        background: 'var(--accent-blue)',
                        color: 'white',
                        padding: '0.375rem 0.75rem',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        {guard.sites.length} Sites
                      </span>
                    </div>
                    <hr style={{ borderColor: 'var(--border-color)', margin: '0.75rem 0' }} />
                    <div style={{ fontSize: '0.85rem' }}>
                      {guard.sites.map((site, siteIndex) => (
                        <div 
                          key={siteIndex}
                          className="d-flex align-items-start gap-2 mb-2"
                          style={{
                            padding: '0.5rem',
                            background: 'var(--surface-glass)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          <div style={{ 
                            minWidth: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '50%',
                            background: 'var(--accent-blue)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            flexShrink: 0
                          }}>
                            {siteIndex + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: 600, 
                              color: 'var(--text-primary)',
                              marginBottom: '0.25rem'
                            }}>
                              {site.siteName}
                            </div>
                            <div style={{ 
                              color: 'var(--text-muted)', 
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <FaCalendar style={{ fontSize: '0.7rem' }} />
                              {site.isSingleDay 
                                ? new Date(site.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : formatDateRange(site.dateFrom, site.dateTo)
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )} */}

      {/* Search and Filters */}
      <Card className="shadow-sm mb-4" style={{
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-xl)'
      }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <Form.Control
                  type="text"
                  placeholder="Search by site name, guard name, or phone number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '2.5rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </Col>
          </Row>
          
          <Row className="g-3 mt-2">
            <Col md={6}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date From
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
            <Col md={6}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date To
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
          </Row>
          
          {(searchTerm || filterDateFrom || filterDateTo) && (
            <div className="mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Clear All Filters
              </Button>
              <span className="ms-3" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Showing {filteredCoverages.length} of {coverages.length} {coverages.length === 1 ? 'assignment' : 'assignments'}
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Coverage List */}
      {loading ? (
        <Card className="shadow-sm text-center py-5" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
            <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Loading coverage assignments...</p>
          </Card.Body>
        </Card>
      ) : filteredCoverages.length > 0 ? (
        <Row className="g-3">
          {filteredCoverages.map((coverage) => (
            <Col key={coverage.id} xs={12}>
              <Card className="coverage-card shadow-sm" style={{
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)'
              }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h5 className="mb-2" style={{ 
                        fontWeight: 700, 
                        color: 'var(--text-primary)',
                        fontSize: '1.25rem'
                      }}>
                        {coverage.siteName}
                      </h5>
                      <div className="d-flex flex-wrap gap-3 align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <FaCalendar style={{ color: 'var(--accent-blue)', fontSize: '0.9rem' }} />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            {coverage.isSingleDay 
                              ? new Date(coverage.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : formatDateRange(coverage.dateFrom, coverage.dateTo)
                            }
                          </span>
                        </div>
                        {coverage.isSingleDay ? (
                          <span className="badge" style={{
                            background: 'var(--success)',
                            color: 'white',
                            padding: '0.375rem 0.75rem',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            Single Day
                          </span>
                        ) : (
                          <span className="badge" style={{
                            background: 'var(--accent-blue-dark)',
                            color: 'white',
                            padding: '0.375rem 0.75rem',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {calculateDuration(coverage.dateFrom, coverage.dateTo)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-responsive coverage-table-wrapper">
                    <table className="coverage-excel-table">
                      <thead>
                        <tr>
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <th key={i}>Guard {i + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="name-row">
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <td key={i}>
                              <div className="guard-cell">
                                <span className="cell-label">Name:</span>
                                <span className="cell-value">{coverage.guards[i]?.name || '—'}</span>
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="phone-row">
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <td key={i}>
                              <div className="guard-cell">
                                <span className="cell-label">Phone:</span>
                                <span className="cell-value">{coverage.guards[i]?.phone || '—'}</span>
                              </div>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="shadow-sm text-center py-5" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <FaClipboardList size={64} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
            <p className="mt-3" style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>
              {coverages.length === 0 
                ? "No coverage assignments yet. Click 'Add Coverage' to create your first assignment."
                : "No coverage assignments found matching your search criteria."}
            </p>
            {coverages.length > 0 && (searchTerm || filterDateFrom || filterDateTo) && (
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Add Coverage Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{
          background: 'var(--surface-glass)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Modal.Title style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
            <FaClipboardList className="me-2" />
            Add New Coverage Assignment
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ background: 'var(--bg-primary)', padding: '2rem' }}>
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
                {error.split('\n').map((line, i) => (
                  line.trim() === '' ? null :
                  line.startsWith('•')
                    ? <div key={i} style={{ paddingLeft: '0.5rem', marginTop: '0.25rem' }}>{line}</div>
                    : <div key={i} style={{ fontWeight: i === 0 ? 600 : 400, marginTop: i > 0 ? '0.5rem' : 0 }}>{line}</div>
                ))}
              </Alert>
            )}

            {duplicateWarning && (
              <Alert variant="warning" onClose={() => setDuplicateWarning('')} dismissible className="mb-3">
                {duplicateWarning.split('\n').map((line, i) => (
                  line.trim() === '' ? null :
                  line.startsWith('•')
                    ? <div key={i} style={{ paddingLeft: '0.5rem', marginTop: '0.25rem' }}>{line}</div>
                    : <div key={i} style={{ fontWeight: i === 0 ? 600 : 400, marginTop: i > 0 ? '0.5rem' : 0 }}>{line}</div>
                ))}
              </Alert>
            )}
            
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Site Name <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter site name"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      color: 'var(--text-primary)',
                      padding: '0.75rem'
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Branch <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={branch ? { value: branch, label: branch } : null}
                    onChange={(selected) => setBranch(selected ? selected.value : '')}
                    loadOptions={loadBranches}
                    placeholder="Search and select branch..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: 'var(--bg-secondary)',
                        borderColor: 'var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        padding: '0.25rem',
                        minHeight: '48px'
                      }),
                      menu: (base) => ({
                        ...base,
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        zIndex: 9999
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? 'var(--accent-blue-light)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: 'var(--text-primary)'
                      }),
                      input: (base) => ({
                        ...base,
                        color: 'var(--text-primary)'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      }),
                      loadingMessage: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      }),
                      noOptionsMessage: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      })
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Region <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={region ? { value: region, label: region } : null}
                    onChange={(selected) => setRegion(selected ? selected.value : '')}
                    loadOptions={loadRegions}
                    placeholder="Search and select region..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: 'var(--bg-secondary)',
                        borderColor: 'var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        padding: '0.25rem',
                        minHeight: '48px'
                      }),
                      menu: (base) => ({
                        ...base,
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        zIndex: 9999
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? 'var(--accent-blue-light)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: 'var(--text-primary)'
                      }),
                      input: (base) => ({
                        ...base,
                        color: 'var(--text-primary)'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      }),
                      loadingMessage: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      }),
                      noOptionsMessage: (base) => ({
                        ...base,
                        color: 'var(--text-muted)'
                      })
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Number of Guards <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={numColumns}
                    onChange={(e) => handleNumColumnsChange(e.target.value)}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      color: 'var(--text-primary)',
                      padding: '0.75rem'
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {isSingleDay ? 'Date' : 'Start Date'} <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      color: 'var(--text-primary)',
                      padding: '0.75rem'
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              
              {!isSingleDay && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      End Date <span style={{ color: 'var(--danger)' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        color: 'var(--text-primary)',
                        padding: '0.75rem'
                      }}
                      required
                    />
                  </Form.Group>
                </Col>
              )}
              
              <Col md={isSingleDay ? 8 : 4}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)', visibility: 'hidden' }}>
                    Placeholder
                  </Form.Label>
                  <div 
                    onClick={() => {
                      setIsSingleDay(!isSingleDay);
                      if (!isSingleDay) {
                        setDateTo(dateFrom);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.875rem 1rem',
                      background: isSingleDay ? 'var(--accent-blue-dark)' : 'var(--bg-secondary)',
                      border: `2px solid ${isSingleDay ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      userSelect: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${isSingleDay ? 'white' : 'var(--border-color)'}`,
                      background: isSingleDay ? 'var(--accent-blue)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}>
                      {isSingleDay && (
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 12 12" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M2 6L5 9L10 3" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: isSingleDay ? 'white' : 'var(--text-primary)',
                        fontSize: '0.95rem',
                        marginBottom: '0.15rem'
                      }}>
                        Single Day Assignment
                      </div>
                      <div style={{ 
                        color: isSingleDay ? 'rgba(255, 255, 255, 0.8)' : 'var(--text-muted)', 
                        fontSize: '0.75rem',
                        lineHeight: '1.3'
                      }}>
                        Check this if the guard is working only for this specific day
                      </div>
                    </div>
                  </div>
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <hr style={{ borderColor: 'var(--border-color)', margin: '1.5rem 0' }} />
                <h6 style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1rem' }}>
                  Guard Information
                </h6>
              </Col>
              
              {guards.map((guard, index) => (
                <React.Fragment key={index}>
                  <Col xs={12}>
                    <div style={{
                      background: 'var(--surface-glass)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span style={{ 
                          fontWeight: 600, 
                          color: 'var(--accent-blue)',
                          fontSize: '0.9rem'
                        }}>
                          Guard {index + 1}
                        </span>
                      </div>
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Control
                            type="text"
                            placeholder="Guard name"
                            value={guard.name}
                            onChange={(e) => handleGuardChange(index, 'name', e.target.value)}
                            style={{
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              color: 'var(--text-primary)',
                              padding: '0.625rem'
                            }}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            type="tel"
                            placeholder="Phone number"
                            value={guard.phone}
                            onChange={(e) => handleGuardChange(index, 'phone', e.target.value)}
                            style={{
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              color: 'var(--text-primary)',
                              padding: '0.625rem'
                            }}
                          />
                        </Col>
                      </Row>
                      
                      {/* Payment Information */}
                      <Row className="g-2 mt-2">
                        <Col xs={12}>
                          <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                            Payment Status
                          </Form.Label>
                        </Col>
                        <Col md={6}>
                          <div style={{
                            display: 'flex',
                            gap: '0.75rem'
                          }}>
                            <div
                              onClick={() => {
                                handleGuardChange(index, 'payType', 'PAYABLE');
                                if (!guard.amount || guard.amount === 0) {
                                  handleGuardChange(index, 'amount', 500);
                                }
                              }}
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.625rem 0.875rem',
                                background: guard.payType === 'PAYABLE' ? 'var(--accent-blue-dark)' : 'var(--bg-secondary)',
                                border: `2px solid ${guard.payType === 'PAYABLE' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: `2px solid ${guard.payType === 'PAYABLE' ? 'white' : 'var(--border-color)'}`,
                                background: guard.payType === 'PAYABLE' ? 'var(--accent-blue)' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.2s ease'
                              }}>
                                {guard.payType === 'PAYABLE' && (
                                  <div style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: 'white'
                                  }} />
                                )}
                              </div>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: guard.payType === 'PAYABLE' ? 'white' : 'var(--text-primary)'
                              }}>
                                Payable
                              </span>
                            </div>
                            
                            <div
                              onClick={() => {
                                handleGuardChange(index, 'payType', 'NON_PAYABLE');
                                handleGuardChange(index, 'amount', 0);
                              }}
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.625rem 0.875rem',
                                background: guard.payType === 'NON_PAYABLE' ? 'var(--accent-blue-dark)' : 'var(--bg-secondary)',
                                border: `2px solid ${guard.payType === 'NON_PAYABLE' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: `2px solid ${guard.payType === 'NON_PAYABLE' ? 'white' : 'var(--border-color)'}`,
                                background: guard.payType === 'NON_PAYABLE' ? 'var(--accent-blue)' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.2s ease'
                              }}>
                                {guard.payType === 'NON_PAYABLE' && (
                                  <div style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: 'white'
                                  }} />
                                )}
                              </div>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: guard.payType === 'NON_PAYABLE' ? 'white' : 'var(--text-primary)'
                              }}>
                                Non-payable
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                              Amount (KSh)
                            </Form.Label>
                            <Form.Control
                              type="number"
                              placeholder="Enter amount"
                              value={guard.amount || ''}
                              onChange={(e) => handleGuardChange(index, 'amount', parseFloat(e.target.value) || 0)}
                              disabled={guard.payType === 'NON_PAYABLE'}
                              style={{
                                background: guard.payType === 'PAYABLE' ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-md)',
                                color: guard.payType === 'PAYABLE' ? 'var(--text-primary)' : 'var(--text-muted)',
                                padding: '0.625rem',
                                opacity: guard.payType === 'PAYABLE' ? 1 : 0.6,
                                cursor: guard.payType === 'PAYABLE' ? 'text' : 'not-allowed'
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </React.Fragment>
              ))}
            </Row>
          </Modal.Body>
          <Modal.Footer style={{
            background: 'var(--surface-glass)',
            borderTop: '1px solid var(--border-color)'
          }}>
            <Button variant="secondary" onClick={handleCloseModal}>
              <FaTimes className="me-2" />
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              <FaSave className="me-2" />
              Create Coverage
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Coverage;
